import React, { useEffect, useRef } from 'react';

/*
 * RiskLo AI's mark: a dark glass disc with a thin brand-gradient ring and a
 * small equity-line glyph. The light on the glass follows the pointer (when
 * `track`); while thinking, the ring speeds up and the line traces itself.
 * Pure CSS/SVG, no images.
 */

const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_e) { return false; }
};

let gradSeq = 0;

export default function RiskloOrb({ size = 40, mood = 'idle', track = false, className = '' }) {
  const ref = useRef(null);
  const gradId = useRef(`rl-orb-grad-${gradSeq += 1}`);

  useEffect(() => {
    if (!track || reducedMotion()) return undefined;
    let frame = 0;
    let last = null;
    const onMove = (e) => {
      last = e;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const el = ref.current;
        if (!el || !last) return;
        const r = el.getBoundingClientRect();
        const dx = last.clientX - (r.left + r.width / 2);
        const dy = last.clientY - (r.top + r.height / 2);
        const dist = Math.hypot(dx, dy) || 1;
        const reach = Math.min(1, dist / 300);
        el.style.setProperty('--lx', `${(50 + (dx / dist) * reach * 22).toFixed(1)}%`);
        el.style.setProperty('--ly', `${(50 + (dy / dist) * reach * 22).toFixed(1)}%`);
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => { window.removeEventListener('pointermove', onMove); if (frame) cancelAnimationFrame(frame); };
  }, [track]);

  return (
    <span ref={ref} className={`rl-orb is-${mood === 'thinking' ? 'thinking' : 'idle'} ${className}`} style={{ '--orb': `${size}px` }} aria-hidden="true">
      <span className="rl-orb-glow" />
      <span className="rl-orb-ring" />
      <span className="rl-orb-core">
        <svg className="rl-orb-mark" viewBox="0 0 24 24" fill="none">
          <defs>
            <linearGradient id={gradId.current} x1="0" y1="1" x2="1" y2="0">
              <stop offset="0%" stopColor="#c7d2fe" />
              <stop offset="100%" stopColor="#ffffff" />
            </linearGradient>
          </defs>
          <path className="rl-orb-line" d="M4 16.5 8.5 12l3 2.8L16 9l4 2.5" stroke={`url(#${gradId.current})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" pathLength="100" />
          <circle className="rl-orb-dot" cx="20" cy="11.5" r="1.6" fill="#fff" />
        </svg>
      </span>
    </span>
  );
}
